import React, { useCallback, useEffect, useState } from 'react'
import classNames from 'classnames'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAppSelector } from '../../../store/hooks'
import {
  selectCrossClientLandingFinishedExecutionsLoading,
  selectCrossClientLandingRunningExecutionsLoading,
  selectCrossClientLandingRunningExecutionCount,
  selectCrossClientLandingNewExecution,
  selectCrossClientInitLoading,
  selectCrossClientEvaluateLoading,
} from '../../../store/crossClient/crossClientLanding'
import { CrossClientQueryDataSource, CrossClientQueryExecution, CrossClientQueryLogicalQuestionDecomposition } from '../../../models/CrossClient'
import { CrossClientLandingFooter } from './Footer/CrossClientLandingFooter'
import { CrossClientLandingInitialization } from './CrossClientLandingInitialization'
import { CrossClientLimit } from './Limit/CrossClientLimit'
import { useCrossClientRemainingQueries } from './Limit/hooks'
import { TrackEvent, useTracking } from '../../../apps/professional-app/fa/common/tracking'
import { PredefinedCrossClientQuery } from '../Predefined/crossClientPredefinedQueries'
import { useCrossClientQueryLandingExecution } from './hooks'
import { CrossClientQueryInput, MAX_USER_QUESTION_LENGTH } from './CrossClientQueryInput'
import { CrossClientQueryDecomposition } from './CrossClientQueryDecomposition'
import { CrossClientQueryError } from './CrossClientQueryError'
import { CrossClientQueryLoadingState } from './CrossClientQueryLoadingState'
import './CrossClientLanding.scss'

const MAX_CONCURRENT_EXECUTIONS = 3

export const CrossClientLanding = () => {
  const { tracked, trackingClient } = useTracking()

  const { state: initialPredefinedExecutionState } = useLocation()
  const navigate = useNavigate()

  const finishedExecutionsLoading = useAppSelector(selectCrossClientLandingFinishedExecutionsLoading)
  const runningExecutionsLoading = useAppSelector(selectCrossClientLandingRunningExecutionsLoading)
  const runningExecutionCount = useAppSelector(selectCrossClientLandingRunningExecutionCount)
  const newExecution = useAppSelector(selectCrossClientLandingNewExecution)
  const initLoading = useAppSelector(selectCrossClientInitLoading)
  const evaluateLoading = useAppSelector(selectCrossClientEvaluateLoading)

  const { remainingCcQueriesPerWeek } = useCrossClientRemainingQueries()

  const [userQuestion, setUserQuestion] = useState<string>('')
  const [predefinedExecution, setPredefinedExecution] = useState<{ description: string; decomposition: CrossClientQueryLogicalQuestionDecomposition } | null>(
    () => {
      if (initialPredefinedExecutionState?.userQuestion && initialPredefinedExecutionState?.decomposition) {
        return { description: initialPredefinedExecutionState.userQuestion, decomposition: initialPredefinedExecutionState.decomposition }
      }
      return null
    },
  )
  const [inputHovered, setInputHovered] = useState<boolean>(false)
  const [showFooter, setShowFooter] = useState<boolean>(true)
  const [sources, setSources] = useState<CrossClientQueryDataSource[]>([])

  const { initError, initializeCrossClientExecution, evaluateCrossClientExecution, resetExecution } = useCrossClientQueryLandingExecution()

  const atomicClauses = predefinedExecution?.decomposition.atomicClauses ?? newExecution?.decomposition?.atomicClauses ?? []
  const nnfDecomposition = newExecution?.nnfDecomposition
  const numberOfClauses = atomicClauses.length
  const tooManyConcurrentExecutions = runningExecutionCount >= MAX_CONCURRENT_EXECUTIONS
  const cannotExecuteNew = !remainingCcQueriesPerWeek || tooManyConcurrentExecutions
  const buttonDisabled = cannotExecuteNew || userQuestion.trim().length < 3 || userQuestion.trim().length > MAX_USER_QUESTION_LENGTH

  const handleConfirm = tracked(
    async () => {
      if (predefinedExecution) {
        const execution = await initializeCrossClientExecution(predefinedExecution.description, predefinedExecution.decomposition)
        if (!execution) {
          return
        }
        await evaluateCrossClientExecution(execution.executionId, sources)
      } else if (newExecution) {
        await evaluateCrossClientExecution(newExecution.executionId, sources)
      }
    },
    {
      eventName: TrackEvent.X_CLIENT_QUERY_CONFIRMED,
      eventDetails: { type: predefinedExecution ? 'pre-defined' : 'custom', query: predefinedExecution ? predefinedExecution.description : userQuestion },
    },
  )

  const restart = useCallback(() => {
    resetExecution()
    setUserQuestion('')
    setPredefinedExecution(null)
    setShowFooter(true)
    navigate('/cross-client', { replace: true, state: null })
  }, [resetExecution, navigate])

  const handleRestart = tracked(restart, {
    eventName: TrackEvent.X_CLIENT_QUERY_DENIED,
    eventDetails: { type: predefinedExecution ? 'pre-defined' : 'custom', query: predefinedExecution ? predefinedExecution.description : userQuestion },
  })

  const handleInitializeCustomCrossClientQuery = tracked(
    async () => {
      navigate('/cross-client', { state: { queryInProgress: true } })
      await initializeCrossClientExecution(userQuestion)
    },
    {
      eventName: TrackEvent.X_CLIENT_QUERY_SUBMITTED,
      eventDetails: {
        type: 'custom',
        query: userQuestion,
        sources: sources.map((source) => (source.type === 'integration' ? source.integrationType : source.type)),
      },
    },
  )

  const handlePredefinedCrossClientQuerySelected = tracked(
    async (predefinedQuery: PredefinedCrossClientQuery) => {
      navigate('/cross-client', { state: { queryInProgress: true } })
      setPredefinedExecution({ description: predefinedQuery.description, decomposition: predefinedQuery.decomposition })
    },
    {
      eventName: TrackEvent.X_CLIENT_QUERY_SUBMITTED,
      eventDetails: {
        type: 'pre-defined',
      },
    },
    {
      eventDetailsExtractor: (predefinedQuery: PredefinedCrossClientQuery) => {
        return { query: predefinedQuery.description }
      },
    },
  )

  const handleExecutionRerun = tracked(
    async (selectedExecution: CrossClientQueryExecution) => {
      const { userQuestion, decomposition } = selectedExecution
      if (!decomposition) {
        return
      }
      navigate('/cross-client', { state: { queryInProgress: true } })
      setPredefinedExecution({ description: userQuestion, decomposition })
    },
    {
      eventName: TrackEvent.X_CLIENT_QUERY_RERUN,
      eventDetails: {
        type: 'pre-defined',
      },
    },
    {
      eventDetailsExtractor: (selectedExecution: CrossClientQueryExecution) => {
        return { executionId: selectedExecution.id }
      },
    },
  )

  useEffect(() => {
    trackingClient.track({
      eventName: TrackEvent.X_CLIENT_OPENED,
    })
  }, [trackingClient])

  useEffect(() => {
    if (!initialPredefinedExecutionState?.queryInProgress && (newExecution || predefinedExecution)) {
      restart()
    }
  }, [initialPredefinedExecutionState?.queryInProgress, newExecution, predefinedExecution, restart])

  return (
    <>
      <CrossClientLandingInitialization />
      <div className='truly-full-size-content d-flex flex-column align-items-center zocks-gap-4 cross-client-landing-container'>
        <div className='d-flex flex-column gap-3 align-items-center'>
          <div className='paragraph-small zocks-mt-4 py-1 zocks-px-0.75 text-uppercase zocks-border-1.0 medium zocks-text-gray-700 cross-client-landing-header'>
            Client Queries
          </div>
          <div className='d-flex flex-column gap-2 text-center'>
            <h2 className='medium'>Find the right clients for every opportunity</h2>
            <div className='paragraph-medium'>Ask questions in plain language. Your AI Assistant delivers results in seconds.</div>
            <CrossClientLimit />
          </div>
        </div>
        <div className='d-flex flex-column w-100 align-items-center position-relative'>
          {tooManyConcurrentExecutions ? (
            <div className='d-flex zocks-gap-0.75 px-4 zocks-py-0.75 zocks-bg-color-cyan w-100 zocks-border-1.25 cross-client-query-limit position-absolute'>
              <i className='bi bi-clock-history fs-6' />
              <div className='paragraph-small'>
                <strong>Query limit reached ({MAX_CONCURRENT_EXECUTIONS} running).</strong> Stop a query or wait for one to finish to continue.
              </div>
            </div>
          ) : null}
          <div
            className={classNames('d-flex flex-column w-100 p-4 gap-3 cross-client-input cross-client-transition zocks-border-1.25 border border-1 bg-white', {
              'cross-client-input-no-shadow': cannotExecuteNew,
              'zocks-bg-color-gray-200': !remainingCcQueriesPerWeek,
              'zocks-border-color-gray-200 cross-client-input-initial-shadow': !cannotExecuteNew && !userQuestion.trim() && !newExecution && !inputHovered,
              'cross-client-input-hover cross-client-input-initial-shadow': !cannotExecuteNew && !userQuestion.trim() && !newExecution && inputHovered,
              'zocks-border-color-gray-200 cross-client-input-decomp-shadow': !cannotExecuteNew && !!userQuestion.trim() && newExecution,
              'zocks-border-color-primary cross-client-input-purple-shadow': !cannotExecuteNew && !!userQuestion.trim() && !newExecution,
            })}
            onMouseEnter={() => setInputHovered(true)}
            onMouseLeave={() => setInputHovered(false)}
          >
            {!predefinedExecution ? (
              <CrossClientQueryInput
                userQuestion={userQuestion}
                onQuestionChange={setUserQuestion}
                onSubmit={handleInitializeCustomCrossClientQuery}
                disabled={cannotExecuteNew || initLoading || !!newExecution}
                submitDisabled={buttonDisabled}
                isLoading={initLoading}
                hasExecution={!!newExecution}
                noRemainingQueries={!remainingCcQueriesPerWeek}
              />
            ) : null}
            {initLoading && !predefinedExecution ? <CrossClientQueryLoadingState /> : null}
            {initError ? (
              <CrossClientQueryError onRestart={handleRestart} />
            ) : numberOfClauses ? (
              <CrossClientQueryDecomposition
                atomicClauses={atomicClauses}
                nnfDecomposition={nnfDecomposition}
                sources={sources}
                onSourcesChange={setSources}
                onRestart={handleRestart}
                onConfirm={handleConfirm}
                isLoading={initLoading || evaluateLoading}
              />
            ) : null}
          </div>
        </div>
        {showFooter ? (
          <div
            className={classNames('d-flex flex-column pt-4 align-items-center zocks-gap-2 w-100 fade', {
              'opacity-0': initLoading || newExecution || predefinedExecution,
              'opacity-100': !initLoading && !newExecution && !predefinedExecution,
            })}
            onTransitionEnd={() => setShowFooter(!initLoading && !newExecution && !predefinedExecution)}
          >
            {!finishedExecutionsLoading && !runningExecutionsLoading ? (
              <CrossClientLandingFooter
                runDisabled={cannotExecuteNew}
                onPredefinedQuerySelect={handlePredefinedCrossClientQuerySelected}
                onRerun={handleExecutionRerun}
              />
            ) : null}
          </div>
        ) : null}
      </div>
    </>
  )
}
